import { Box, Grid } from "@mui/material";
import { useFormik } from "formik";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";

import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useGetFlagList } from "../../../../../../../queries/flags/flags/use-get-flag-list";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import MaskMultiSelectScroll from "../../../../../../../components/MaskMultiSelectScroll";
import { useAddPatientFlagByPatientId } from "../../../../../../../queries/flags/patientFlags/use-add-flags-by-patient-id";
// import * as Yup from "yup";

interface UpsertPatientFlagDrawerProps {
  disclosureHost: DisclosureHost;
  flag: any;
  setFlag: (flag: any[]) => void;
  refetchPatientFlags: () => void;
}

const UpsertPatientFlagDrawer = ({
  disclosureHost,
  flag,
  setFlag,
  refetchPatientFlags,
}: UpsertPatientFlagDrawerProps) => {
  const showSnackbar = useSnackbar();
  const { mutateAsync: addPatientFlagByPatientId } =
    useAddPatientFlagByPatientId();

  const { patientId } = useParams();

  const [flagSearchKey, setFlagSearchKey] = useDebounce<string>("");
  const [flagPagination, setFlagPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  // this is common for all the flags
  const { data: flagDropdownList, isFetching: flagListFetching } =
    useGetFlagList({
      pagination: flagPagination,
      xTENANTID: getXtenantId(),
      gridSorting: [],
      name: flagSearchKey,
      active: true,
      archive: false,
    });

  const formik = useFormik({
    initialValues: {
      flag: flag || undefined,
    },
    onSubmit: handleCreatePatientFlag,
    // validationSchema: Yup.object({
    //   flag: Yup.array()
    //     .required("Flag is required")
    //     .min(1, "Select at least one flag")
    //     .label("Flag"),
    // }),
    enableReinitialize: true,
  });

  async function handleCreatePatientFlag(values?: any) {
    const flagUuids = values?.flag?.map((flag: any) => flag.value) || [];

    const payload = {
      patientMultipleFlagsRequest: {
        patientUuid: patientId,
        flagUuids: flagUuids,
      },
      xTENANTID: getXtenantId(),
    };

    showToastAndResetForm(addPatientFlagByPatientId(payload));
  }
  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        setFlag(formik.values?.flag || []);
        // queryClient.invalidateQueries({
        //   queryKey: ["patient-flag-list"],
        // });
        refetchPatientFlags();
        closeAndReset();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }
  function closeAndReset() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost?.opened}
      onClose={closeAndReset}
      dialogTitle="Add Patient Flag"
      dialogAction={
        <StyledButtonV2
          label="Save"
          variant="contained"
          onClick={formik.submitForm}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "30vw",
          height: "20vh",
        },
      }}
    >
      <Box pt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MaskMultiSelectScroll
              required
              limitTags={3}
              loading={flagListFetching}
              pagination={flagPagination}
              setPagination={setFlagPagination}
              totalElements={flagDropdownList?.data?.page?.totalElements}
              onSearch={(val) => {
                setFlagSearchKey(val);
                setFlagPagination((p) => ({ ...p, page: 0 }));
              }}
              optionWithCheckbox={true}
              value={formik.values.flag}
              placeholder="Select Flag"
              options={flagDropdownList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.name,
                color: it?.color,
              }))}
              onChange={(val) => {
                formik.setFieldValue("flag", val);
              }}
              renderCustomLabel={(option) => (
                <Flex alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: "30px",
                      height: "20px",
                      backgroundColor: option.color,
                      borderRadius: "2px",
                      flexShrink: 0,
                    }}
                  />
                  <Text>{option.label}</Text>
                </Flex>
              )}
              error={formik.touched.flag && (formik.errors.flag as any)}
            />
          </Grid>
        </Grid>
      </Box>
      {/* </StyledDrawer> */}
    </StyledFormModal>
  );
};

export default UpsertPatientFlagDrawer;
